// Importa um prompt colado (markdown ou HTML com tags) e devolve as sessões
// equivalentes ao formato usado por compile_sections no backend.

export interface ParsedSection { name: string; content: string }

const OPEN_TAG = /^<([a-z][a-z0-9-]*)>$/;
const CLOSE_TAG = /^<\/([a-z][a-z0-9-]*)>$/;
// \s* (não \s+): aceita heading sem espaço também — "#teste" importa igual "# teste".
const HEADING = /^(#{1,6})\s*(.+)$/;
// Só nível 1 abre sessão nova; ## em diante é subseção e fica no texto da sessão.
const H1 = /^#(?!#)\s*(.+)$/;

function titleFromSlug(slug: string): string {
  return slug.split("-").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

/** Espelha compile_sections do backend — pra copiar o prompt completo pro clipboard. */
export function compileSections(sections: { name: string; content: string }[]): string {
  const blocks: string[] = [];
  for (const s of sections) {
    const name = s.name.trim();
    const content = s.content.trim();
    if (!name && !content) continue;
    if (!name) { blocks.push(content); continue; }
    blocks.push(content ? `# ${name}\n${content}` : `# ${name}`);
  }
  return blocks.join("\n\n");
}

// Só reconhece tags que ocupam a linha inteira (o formato que o compile_sections
// antigo gerava). Uma tag aninhada nunca cria uma sessão nova: conta
// a profundidade e, enquanto ela não volta a 0, tudo é texto literal da
// sessão de nível 1 — inclusive tags fechando fora de ordem (crossing tags).
function parseHtmlPrompt(raw: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  let plain: string[] = [];
  let depth = 0;
  let name = "";
  let buffer: string[] = [];

  function flushPlain() {
    const text = plain.join("\n").trim();
    if (text) sections.push({ name: "", content: text });
    plain = [];
  }

  for (const line of raw.split("\n")) {
    const trimmed = line.trim();
    if (depth === 0) {
      const open = OPEN_TAG.exec(trimmed);
      if (open) {
        flushPlain();
        name = titleFromSlug(open[1]);
        buffer = [];
        depth = 1;
      } else {
        plain.push(line);
      }
      continue;
    }
    if (OPEN_TAG.test(trimmed)) {
      depth += 1;
      buffer.push(line);
    } else if (CLOSE_TAG.test(trimmed)) {
      depth -= 1;
      if (depth === 0) sections.push({ name, content: buffer.join("\n").trim() });
      else buffer.push(line);
    } else {
      buffer.push(line);
    }
  }
  if (depth > 0) sections.push({ name, content: buffer.join("\n").trim() });
  flushPlain();
  return sections;
}

// Cabeçalho nível 1 (#) abre sessão nova; ##, ###... ficam como texto literal
// dentro da sessão corrente (subseções markdown do próprio usuário).
function parseMarkdownPrompt(raw: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  let name = "";
  let buffer: string[] = [];
  let started = false;

  function flush() {
    const content = buffer.join("\n").trim();
    if (name || content) sections.push({ name, content });
  }

  for (const line of raw.split("\n")) {
    const m = H1.exec(line.trim());
    if (m) {
      if (started || buffer.some((l) => l.trim())) flush();
      name = m[1].trim();
      buffer = [];
      started = true;
    } else {
      buffer.push(line);
    }
  }
  flush();
  return sections;
}

/** {{ variavel }} e [[ campo ]] não colidem com heading/tag e passam intactos. */
export function parsePrompt(raw: string): ParsedSection[] {
  const lines = raw.split("\n").map((l) => l.trim());
  if (lines.some((l) => OPEN_TAG.test(l) || CLOSE_TAG.test(l))) return parseHtmlPrompt(raw);
  if (lines.some((l) => HEADING.test(l))) return parseMarkdownPrompt(raw);
  const text = raw.trim();
  return text ? [{ name: "", content: text }] : [];
}
